import React, { FC } from 'react';
import MuiCard from '@mui/material/Card';
import { useTheme } from '@mui/material/styles';
import { contentSpacing } from '../../tokens/base/contentStyles';
import { DSCardProps } from './Card.types';

export const Card: FC<DSCardProps> = ({
  variant = 'outlined',     // Fluent 2 default: outlined cards
  elevation = 'default',
  interactive = false,
  padding = 'default',
  children,
  sx,
  ...props
}) => {
  const theme = useTheme();

  const elevationMap = {
    flat: 0,
    default: 2,
    raised: 4
  };

  const paddingMap = {
    none: 0,
    compact: contentSpacing.gutter,
    default: contentSpacing.gutter
  };

  const variantStyles = {
    outlined: {
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: 'none',
      backgroundColor: theme.palette.background.paper
    },
    elevated: {
      border: 'none',
      boxShadow: theme.shadows[elevationMap[elevation]]
    },
    flat: {
      border: 'none',
      boxShadow: 'none',
      backgroundColor: 'transparent'
    }
  };

  const interactiveStyles = interactive ? {
    cursor: 'pointer',
    transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: variant === 'elevated' ? theme.shadows[4] : undefined,
      borderColor: variant === 'outlined' ? theme.palette.atlas?.ui?.control ?? theme.palette.primary.main : undefined,
      transform: 'translateY(-2px)'
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.atlas?.ui?.focus ?? theme.palette.primary.main}`,
      outlineOffset: '2px'
    }
  } : {};

  return (
    <MuiCard
      sx={{
        borderRadius: 2,  // 8px (Fluent 2 card radius)
        p: paddingMap[padding],
        ...variantStyles[variant],
        ...interactiveStyles,
        ...sx
      }}
      {...props}
    >
      {children}
    </MuiCard>
  );
};
